/**
 * Mock real-time data service for injection-molding shots.
 *
 * `fetchShots` is the SINGLE integration point with the backend: today it
 * synthesizes realistic shot data locally, but it is the only function that
 * needs to change when wiring the real AWS data source (see the TODO below).
 * Both the initial history load and the live polling in `useMachineShots` go
 * through it, so swapping the implementation lights up the whole dashboard.
 */

import { useEffect, useState } from 'react';
import {
  CMT_MACHINES,
  PARAM_META,
  type MachineShot,
  type ParamKey,
} from './injectionMachines';

/* ------------------------------------------------------------------ */
/*  Mock generation (to be replaced by a real backend call)            */
/* ------------------------------------------------------------------ */

/** Nominal operating point + noise amplitude + rounding for each parameter. */
interface Baseline {
  base: number;
  /** Peak-to-peak random noise amplitude. */
  amp: number;
  decimals: number;
}

const BASELINE: Record<ParamKey, Baseline> = {
  // Pressure (bar)
  injectionPressure: { base: 1150, amp: 40, decimals: 0 },
  peakPressure: { base: 1320, amp: 55, decimals: 0 },
  holdingPressure: { base: 720, amp: 30, decimals: 0 },
  backPressure: { base: 90, amp: 8, decimals: 0 },
  // Temperature (°C)
  meltTemp: { base: 235, amp: 3, decimals: 1 },
  nozzleTemp: { base: 240, amp: 3, decimals: 1 },
  moldTempFront: { base: 65, amp: 2, decimals: 1 },
  moldTempRear: { base: 62, amp: 2, decimals: 1 },
  // Timing (s)
  cycleTime: { base: 28.5, amp: 0.8, decimals: 1 },
  injectionTime: { base: 2.1, amp: 0.15, decimals: 2 },
  holdingTime: { base: 6.0, amp: 0.2, decimals: 2 },
  coolingTime: { base: 14.0, amp: 0.4, decimals: 1 },
  // Position (mm)
  shotSize: { base: 58.0, amp: 0.6, decimals: 1 },
  cushion: { base: 6.5, amp: 0.4, decimals: 2 },
  switchoverPosition: { base: 12.0, amp: 0.5, decimals: 1 },
  screwPosition: { base: 0.8, amp: 0.3, decimals: 2 },
};

/** Spacing between historical shots when seeding the initial batch. */
const HISTORY_SPACING_MS = 4000;

/** Per-machine evolving state so successive fetches produce smooth streams. */
interface MachineState {
  shotNo: number;
  last: Record<ParamKey, number> | null;
}

const machineStates = new Map<string, MachineState>();

function getState(machineId: string): MachineState {
  let s = machineStates.get(machineId);
  if (!s) {
    s = { shotNo: 0, last: null };
    machineStates.set(machineId, s);
  }
  return s;
}

/** Stable per-machine multiplier so the two machines run slightly differently. */
function machineMultiplier(machineId: string): number {
  let hash = 0;
  for (let i = 0; i < machineId.length; i += 1) {
    hash = (hash * 31 + machineId.charCodeAt(i)) & 0xffff;
  }
  // Maps to roughly 0.96 – 1.06
  return 0.96 + (hash % 100) / 1000;
}

function round(value: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

/** Resolve the mold name configured for a machine (falls back gracefully). */
function moldNameFor(machineId: string): string {
  return (
    CMT_MACHINES.find((m) => m.id === machineId)?.defaultMoldName ?? 'Unknown'
  );
}

/** Produce the next shot for a machine, evolving from its previous values. */
function generateNext(
  machineId: string,
  state: MachineState,
  timestamp: number,
): MachineShot {
  const mult = machineMultiplier(machineId);
  const values = {} as Record<ParamKey, number>;

  for (const { key } of PARAM_META) {
    const b = BASELINE[key];
    const target = b.base * mult;
    const prev = state.last?.[key] ?? target;
    // Mean-reverting random walk: drift toward target + bounded noise.
    const noise = (Math.random() - 0.5) * b.amp;
    const next = prev + (target - prev) * 0.25 + noise;
    values[key] = round(Math.max(0, next), b.decimals);
  }

  state.shotNo += 1;
  state.last = values;

  return {
    id: timestamp * 1000 + (state.shotNo % 1000),
    machineId,
    shotNo: state.shotNo,
    shotTime: timestamp,
    moldName: moldNameFor(machineId),
    ...values,
  };
}

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Fetch a batch of `count` shots for a machine, oldest first.
 *
 * TODO(aws): replace the body below with the real data source, e.g.
 *   const res = await fetch(`/api/machines/${machineId}/shots?count=${count}`, ...);
 *   return (await res.json()) as MachineShot[];
 * Everything upstream (the polling hook and the dashboard) already consumes
 * `MachineShot[]`, so no other change is required.
 */
export async function fetchShots(
  machineId: string,
  count: number,
): Promise<MachineShot[]> {
  await delay(150); // simulate network latency
  const state = getState(machineId);
  const now = Date.now();
  const shots: MachineShot[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const ts = now - i * HISTORY_SPACING_MS;
    shots.push(generateNext(machineId, state, ts));
  }
  return shots;
}

/* ------------------------------------------------------------------ */
/*  Polling hook                                                       */
/* ------------------------------------------------------------------ */

/** Load state for the shot stream. */
export type ShotStreamStatus = 'loading' | 'live' | 'error';

export interface UseMachineShotsResult {
  /** Most recent shots, oldest first (chart-friendly ordering). */
  shots: MachineShot[];
  /** The newest shot, or `null` before the first load. */
  latest: MachineShot | null;
  status: ShotStreamStatus;
  /** True once the live stream is actively updating. */
  isLive: boolean;
}

/** How many recent shots to retain in memory. */
const MAX_SHOTS = 40;
/** How many shots to seed the initial history with. */
const INITIAL_COUNT = 24;

export interface UseMachineShotsOptions {
  /** Polling interval in milliseconds (default 4000). */
  intervalMs?: number;
}

/**
 * Subscribe to a machine's live shot stream. Loads an initial history, then
 * appends a fresh shot every `intervalMs`, retaining the latest `MAX_SHOTS`.
 * Resets cleanly when `machineId` changes and clears its timer on unmount.
 */
export function useMachineShots(
  machineId: string,
  { intervalMs = 4000 }: UseMachineShotsOptions = {},
): UseMachineShotsResult {
  const [shots, setShots] = useState<MachineShot[]>([]);
  const [status, setStatus] = useState<ShotStreamStatus>('loading');

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    setStatus('loading');
    setShots([]);

    async function start() {
      try {
        const initial = await fetchShots(machineId, INITIAL_COUNT);
        if (cancelled) return;
        setShots(initial.slice(-MAX_SHOTS));
        setStatus('live');

        timer = setInterval(async () => {
          try {
            const next = await fetchShots(machineId, 1);
            if (cancelled || next.length === 0) return;
            setShots((prev) => [...prev, ...next].slice(-MAX_SHOTS));
          } catch {
            if (!cancelled) setStatus('error');
          }
        }, intervalMs);
      } catch {
        if (!cancelled) setStatus('error');
      }
    }

    void start();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [machineId, intervalMs]);

  const latest = shots.length > 0 ? shots[shots.length - 1] : null;

  return { shots, latest, status, isLive: status === 'live' };
}
